/**
 * テンプレートエディタコンポーネント
 *
 * 構造化面接テンプレートの作成・編集UI
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Slider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Science as ScienceIcon
} from '@mui/icons-material';

import { templateService } from '../../services/audioAPI';

/**
 * テンプレートエディタ
 */
export default function TemplateEditor({
  template = null,
  onSave = null,
  onCancel = null,
  open = true,
  embedded = false  // trueの場合、ダイアログなしでコンテンツのみ表示
}) {
  const isNew = !template?.id;

  // フォーム状態
  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    job_category: 'エンジニア',
    experience_level: '中途',
    competencies: [],
    bars_scales: {},
    recommended_questions: {},
    evaluation_guidelines: [],
    scientific_basis: {
      methodology: 'structured_behavioral_interview',
      validity_coefficient: 0.42,
      references: ['Schmidt & Hunter (1998)', 'Taylor & Small (2002)']
    }
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // 初期化
  useEffect(() => {
    if (template) {
      setFormData({
        template_name: template.template_name || '',
        description: template.description || '',
        job_category: template.job_category || 'エンジニア',
        experience_level: template.experience_level || '中途',
        competencies: template.competencies || [],
        bars_scales: template.bars_scales || {},
        recommended_questions: template.recommended_questions || {},
        evaluation_guidelines: template.evaluation_guidelines || [],
        scientific_basis: template.scientific_basis || {
          methodology: 'structured_behavioral_interview',
          validity_coefficient: 0.42
        }
      });
    }
  }, [template]);

  // 基本情報の変更
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // コンピテンシーの追加
  const addCompetency = () => {
    const newId = `comp_${Date.now()}`;
    const newCompetency = {
      id: newId,
      name: '新しいコンピテンシー',
      description: '',
      weight: 0.25
    };

    setFormData(prev => ({
      ...prev,
      competencies: [...prev.competencies, newCompetency],
      bars_scales: {
        ...prev.bars_scales,
        [newId]: {
          '1': { anchor: '要改善', behaviors: [] },
          '2': { anchor: '基本レベル', behaviors: [] },
          '3': { anchor: '期待水準', behaviors: [] },
          '4': { anchor: '優秀', behaviors: [] },
          '5': { anchor: '卓越', behaviors: [] }
        }
      },
      recommended_questions: {
        ...prev.recommended_questions,
        [newId]: []
      }
    }));
  };

  // コンピテンシーの削除（idまたはnameで識別）
  const removeCompetency = (competencyKey) => {
    setFormData(prev => {
      const newBarsScales = { ...prev.bars_scales };
      const newQuestions = { ...prev.recommended_questions };
      delete newBarsScales[competencyKey];
      delete newQuestions[competencyKey];

      return {
        ...prev,
        competencies: prev.competencies.filter(c => (c.id || c.name) !== competencyKey),
        bars_scales: newBarsScales,
        recommended_questions: newQuestions
      };
    });
  };

  // コンピテンシーの更新
  const updateCompetency = (index, field, value) => {
    setFormData(prev => {
      const newCompetencies = [...prev.competencies];
      newCompetencies[index] = {
        ...newCompetencies[index],
        [field]: value
      };
      return { ...prev, competencies: newCompetencies };
    });
  };

  // BARSアンカーの更新
  const updateBARSAnchor = (competencyId, score, field, value) => {
    setFormData(prev => ({
      ...prev,
      bars_scales: {
        ...prev.bars_scales,
        [competencyId]: {
          ...prev.bars_scales[competencyId],
          [score]: {
            ...prev.bars_scales[competencyId]?.[score],
            [field]: value
          }
        }
      }
    }));
  };

  // 推奨質問の追加
  const addQuestion = (competencyId) => {
    setFormData(prev => ({
      ...prev,
      recommended_questions: {
        ...prev.recommended_questions,
        [competencyId]: [
          ...(prev.recommended_questions[competencyId] || []),
          { question: '', question_type: 'behavioral' }
        ]
      }
    }));
  };

  // 推奨質問の削除
  const removeQuestion = (competencyId, questionIndex) => {
    setFormData(prev => ({
      ...prev,
      recommended_questions: {
        ...prev.recommended_questions,
        [competencyId]: prev.recommended_questions[competencyId].filter((_, i) => i !== questionIndex)
      }
    }));
  };

  // 推奨質問の更新
  const updateQuestion = (competencyId, questionIndex, field, value) => {
    setFormData(prev => {
      const newQuestions = [...(prev.recommended_questions[competencyId] || [])];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        [field]: value
      };
      return {
        ...prev,
        recommended_questions: {
          ...prev.recommended_questions,
          [competencyId]: newQuestions
        }
      };
    });
  };

  // 重み付けの正規化
  const normalizeWeights = () => {
    const totalWeight = formData.competencies.reduce((sum, c) => sum + (c.weight || 0), 0);
    if (totalWeight === 0) return;

    setFormData(prev => ({
      ...prev,
      competencies: prev.competencies.map(c => ({
        ...c,
        weight: parseFloat((c.weight / totalWeight).toFixed(2))
      }))
    }));
  };

  // 保存
  const handleSave = async () => {
    if (!formData.template_name.trim()) {
      setError('テンプレート名を入力してください');
      return;
    }

    if (formData.competencies.length === 0) {
      setError('少なくとも1つのコンピテンシーを追加してください');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let result;
      if (isNew) {
        result = await templateService.createTemplate(formData);
      } else {
        result = await templateService.updateTemplate(template.id, formData);
      }

      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      setError('テンプレートの保存に失敗しました');
      console.error('Failed to save template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // 職種カテゴリ
  const jobCategories = ['エンジニア', 'ビジネス', 'クリエイティブ', 'バックオフィス'];
  const experienceLevels = ['新卒', '中途', '管理職', '役員'];

  // エディタコンテンツ
  const editorContent = (
    <Box sx={{ p: embedded ? 0 : 3 }}>
        {/* 科学的根拠 */}
        <Alert severity="info" icon={<ScienceIcon />} sx={{ mb: 3 }}>
          構造化面接テンプレートは予測妥当性を高めます（ρ=.42）。
          BARSを使用することで評価者間の一貫性が向上します。
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 基本情報 */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            基本情報
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="テンプレート名"
                fullWidth
                required
                value={formData.template_name}
                onChange={(e) => handleFieldChange('template_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>職種カテゴリ</InputLabel>
                <Select
                  value={formData.job_category}
                  label="職種カテゴリ"
                  onChange={(e) => handleFieldChange('job_category', e.target.value)}
                >
                  {jobCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>経験レベル</InputLabel>
                <Select
                  value={formData.experience_level}
                  label="経験レベル"
                  onChange={(e) => handleFieldChange('experience_level', e.target.value)}
                >
                  {experienceLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="説明"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* コンピテンシー */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              評価コンピテンシー
            </Typography>
            <Box>
              <Button
                size="small"
                onClick={normalizeWeights}
                sx={{ mr: 1 }}
              >
                重み正規化
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCompetency}
              >
                追加
              </Button>
            </Box>
          </Box>

          {formData.competencies.map((competency, index) => (
            <Accordion key={competency.id || `comp_${index}`} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {competency.label || competency.name || '(名称未設定)'}
                  </Typography>
                  <Chip
                    label={`重み: ${(competency.weight * 100).toFixed(0)}%`}
                    size="small"
                  />
                  <Box
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCompetency(competency.id || competency.name);
                    }}
                    sx={{
                      ml: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'error.main',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.7 }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="コンピテンシー名"
                      fullWidth
                      value={competency.name}
                      onChange={(e) => updateCompetency(index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography gutterBottom>重み: {(competency.weight * 100).toFixed(0)}%</Typography>
                    <Slider
                      value={competency.weight}
                      onChange={(_, value) => updateCompetency(index, 'weight', value)}
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="説明"
                      fullWidth
                      multiline
                      rows={2}
                      value={competency.description}
                      onChange={(e) => updateCompetency(index, 'description', e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* BARSアンカー */}
                <Typography variant="subtitle2" gutterBottom>
                  BARS行動アンカー (1-5段階)
                </Typography>
                {[1, 2, 3, 4, 5].map(score => {
                  const competencyKey = competency.id || competency.name;
                  const barsValue = formData.bars_scales[competencyKey]?.[score];
                  // barsValueが文字列の場合（旧形式）とオブジェクトの場合（新形式）を両方対応
                  const anchorText = typeof barsValue === 'string' ? barsValue : (barsValue?.anchor || '');
                  return (
                    <Box key={score} sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        スコア {score}:
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`スコア${score}の行動アンカー`}
                        value={anchorText}
                        onChange={(e) => updateBARSAnchor(competencyKey, score, 'anchor', e.target.value)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  );
                })}

                <Divider sx={{ my: 2 }} />

                {/* 推奨質問 */}
                {(() => {
                  const competencyKey = competency.id || competency.name;
                  // recommended_questionsが配列形式（旧形式）の場合はフィルタリング
                  const questions = Array.isArray(formData.recommended_questions)
                    ? formData.recommended_questions.filter(q => q.competency === competencyKey)
                    : (formData.recommended_questions[competencyKey] || []);
                  return (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">
                          推奨質問
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addQuestion(competencyKey)}
                        >
                          質問追加
                        </Button>
                      </Box>
                      {questions.map((q, qIndex) => (
                        <Box key={qIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={q.question_type || 'behavioral'}
                              onChange={(e) => updateQuestion(competencyKey, qIndex, 'question_type', e.target.value)}
                            >
                              <MenuItem value="behavioral">行動</MenuItem>
                              <MenuItem value="situational">状況</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="質問内容（STAR形式推奨）"
                            value={q.question || ''}
                            onChange={(e) => updateQuestion(competencyKey, qIndex, 'question', e.target.value)}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeQuestion(competencyKey, qIndex)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
          ))}

          {formData.competencies.length === 0 && (
            <Alert severity="warning">
              コンピテンシーを追加してください
            </Alert>
          )}
        </Paper>

        {/* embedded時のアクションボタン */}
        {embedded && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button onClick={onCancel}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isNew ? '作成' : '保存'}
            </Button>
          </Box>
        )}
    </Box>
  );

  // embeddedモードの場合はダイアログなしで返す
  if (embedded) {
    return editorContent;
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {isNew ? '新規テンプレート作成' : 'テンプレート編集'}
        </Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {editorContent}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isNew ? '作成' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
